import type { Channel, MessageBase } from 'stream-chat';

import { toSortedTrainingSessionsByName } from 'src/controllers/moduleController';
import { getGuestInfo, GuestType, isGuestOfTypes } from 'src/user';

import type { LiveChatChannelTrainingSession } from './liveChat.types';

const ALLOWED_GUEST_TYPES: GuestType[] = ['instructor', 'trainee'];
// Private channel IDs follow `<academy hash>-<instructor ID>-<trainee ID>`,
// where the two IDs are numeric. Anchored so it doesn't match public channel
// IDs that merely contain dashes.
const PRIVATE_CHANNEL_ID_REGEX = /^[^-]+-\d+-\d+$/;
export const NOTIFICATION_USER_ID = 'notification';

export const ChannelType = {
  MESSAGING: 'messaging',
  NOTIFICATION: 'notification',
} as const;

export type ChannelAudience = 'public' | 'private';
export type ChannelTypeType = (typeof ChannelType)[keyof typeof ChannelType];
export type TrainingSessionChannelsMap = ReturnType<typeof groupChannelsPerTrainingSession>;
type PrivateChannelRecipientType = GuestType;

export function getStreamUserId(academyHash: string): string | undefined {
  const guest = getGuestInfo();

  if (!guest?.id || !isGuestOfTypes(guest, ALLOWED_GUEST_TYPES)) {
    return;
  }

  return `${academyHash}-${guest.id}`;
}

export function getChannelType(channel: { type: Channel['type'] }): ChannelTypeType {
  return channel.type as ChannelTypeType;
}

export function isNotificationChannel(channel: { type: Channel['type'] }): boolean {
  return getChannelType(channel) === 'notification';
}

/**
 * Build the notification channel CID for the current user. Returns null for
 * instructors since notification channels are only created for trainees.
 *
 * The CID format is deterministic: `notification:<academyHash>-<userId>-notification`.
 * `streamUserId` is the GetStream user id, formatted as `<academyHash>-<userId>`.
 */
export function getNotificationChannelCid(streamUserId: string): string | null {
  const guest = getGuestInfo();

  if (guest?.type !== 'trainee') {
    return null;
  }

  return `notification:${streamUserId}-notification`;
}

export function getChannelAudience(channel: Channel): ChannelAudience {
  if (isChannelPrivate(channel)) {
    return 'private';
  }

  return 'public';
}

/**
 * Get the type of the recipient in a private channel.
 *
 * @param channel Channel to get the recipient type of.
 * @param currentUserId ID of the current user.
 * @returns The recipient type of the private channel, or `null` if not applicable.
 */
export function getPrivateChannelRecipientType(channel: Channel, currentUserId: string): PrivateChannelRecipientType | null {
  if (getChannelAudience(channel) !== 'private') {
    return null;
  }

  if (getChannelType(channel) !== 'messaging') {
    return null;
  }

  const recipientId = Object.keys(channel.state.members).find(memberId => memberId !== currentUserId);

  if (!recipientId) {
    return null;
  }

  return channel.state.members[recipientId].user?.role as GuestType;
}

export function isNotificationMessage(message: Pick<MessageBase, 'user_id' | 'user'>) {
  return (message.user_id ?? message.user?.id) === NOTIFICATION_USER_ID;
}

export function isChannelEmpty(channel: Channel | undefined): boolean {
  // An channel with no message has a default last_message_at date.
  // Using this property avoids an extra HTTP request even though it's not
  // intuitive.
  return channel?.data?.last_message_at === '0001-01-01T00:00:00Z';
}

/**
 * Group {@link LiveChatChannelTrainingSession}s by training session.
 * @param channelTrainingSessions {@link LiveChatChannelTrainingSession}s to group by training session.
 * @returns A map keyed by training session ID, with the list of channels in that training session.
 */
export function groupChannelsPerTrainingSession(channelTrainingSessions: LiveChatChannelTrainingSession[]) {
  return channelTrainingSessions.reduce((trainingSessions, channelTrainingSession) => {
    if (trainingSessions.has(channelTrainingSession.trainingSessionId)) {
      trainingSessions.get(channelTrainingSession.trainingSessionId)!.channels.push(channelTrainingSession);
    } else {
      trainingSessions.set(channelTrainingSession.trainingSessionId, {
        id: channelTrainingSession.trainingSessionId,
        name: channelTrainingSession.trainingSessionName,
        channels: [channelTrainingSession],
      });
    }

    return trainingSessions;
  }, new Map<string, { id: string; name: string; channels: LiveChatChannelTrainingSession[] }>());
}

export function mapTrainingSessionChannelsToFilterOptions(trainingSessionToChannelsMap: TrainingSessionChannelsMap) {
  const trainingSessions = Array.from(trainingSessionToChannelsMap.values());

  return toSortedTrainingSessionsByName(trainingSessions) as typeof trainingSessions;
}

function isChannelPrivate(channel: Channel): boolean {
  if (channel.type === ChannelType.NOTIFICATION) {
    return true;
  }

  // Private channels have the following ID format:
  // `<academy hash>-<instructor ID>-<trainee ID>`
  if (channel.id && PRIVATE_CHANNEL_ID_REGEX.test(channel.id)) {
    return true;
  }

  return false;
}
